import { useIntl } from 'react-intl';
import { Col, Form, FormInstance, Input, Row, Skeleton } from 'antd';
import { Task, TaskPrice } from '@/core/domain/operational/Tasks/Tasks.types';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { InputDecimal } from '@/components/_common';
import TaskPricesForm from '../TaskPricesForm';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import React from 'react';

export interface TaskFormData {
  description: string;
  lifespan: number;
  maintenanceKm: number;
  maintenanceTime: number;
  note: string;
  subAreaId: number;
  taskPriceSet: TaskPrice[];
  temparioCode: string;
  time: number;
  unit: string;
  nbsCode?: string;
}

interface TaskFormProps {
  form: FormInstance;
  edit?: boolean;
  isLoading?: boolean;
  task?: Task;
  isModal?: boolean;
}

const TaskForm = ({
  form,
  edit,
  isLoading,
  task,
  isModal = false,
}: TaskFormProps) => {
  const intl = useIntl();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  return (
    <Form
      form={form}
      name={isModal ? undefined : 'generalTasks'}
      preserve={false}
    >
      <Skeleton active loading={isLoading}>
        <Row gutter={[16, 10]}>
          <Col xs={24} sm={24} md={12} lg={16}>
            <FloatingFormItem
              name="description"
              label={intl.formatMessage({
                id: 'tasks.form.description',
              })}
              rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(200)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem
              name="temparioCode"
              label={intl.formatMessage({
                id: 'tasks.form.temporaryCode',
              })}
              rules={[defaultMaxLenghtFieldError(200)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={12}>
            <FloatingFormItem
              name="subAreaId"
              label={intl.formatMessage({ id: 'tasks.form.area' })}
              rules={[defaultEmptyFieldError]}
            >
              <AreasInfiniteSelectField subAreaSelected={task?.subArea} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={6}>
            <FloatingFormItem
              name="maintenanceKm"
              label={intl.formatMessage({
                id: 'tasks.form.maintenanceKm',
              })}
            >
              <InputDecimal fractions={0} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={6}>
            <FloatingFormItem
              name="nbsCode"
              label={intl.formatMessage({
                id: 'tasks.form.nbsCode',
              })}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem
              name="unit"
              label={intl.formatMessage({
                id: 'tasks.form.unit',
              })}
              rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(2)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem
              name="time"
              label={intl.formatMessage({
                id: 'tasks.form.time',
              })}
              rules={[defaultEmptyFieldError]}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem
              name="lifespan"
              label={intl.formatMessage({
                id: 'tasks.form.lifeSpan',
              })}
            >
              <InputDecimal fractions={0} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={24} lg={16}>
            <FloatingFormItem
              name="note"
              label={intl.formatMessage({
                id: 'tasks.form.note',
              })}
              rules={[defaultMaxLenghtFieldError(700)]}
            >
              <Input.TextArea rows={4} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Skeleton>

      {hasPermissions([Authorities.REGISTER_TASK_PRICE_VIEW]) && (
        <TaskPricesForm form={form} edit={edit} />
      )}
    </Form>
  );
};

export default TaskForm;
